import Routes from '../constants/routes';
import { storageKeys } from './browser-storage/storage-keys';
import isTrustedReturnToOrigin from './is-trusted-return-to-origin';

// A consumer (e.g. AI Studio) sends a signed-out teacher to the login page with
// ?return_to=<trusted origin>&prepare_token=1[&next=<path>]. Some sign-in paths
// leave that URL behind before the sign-in completes — the e-mail confirmation
// link opens a fresh tab, and a login form on another page (the desktop header
// dropdown) signs in where it stands. The request is therefore remembered when a
// login form opens with it and replayed once after the next successful sign-in,
// unless the teacher opens the login page again without it first (the phone
// header icon opens the login page itself). The password-reset tab carries it
// back in the login URL instead. Only the request is stored, never the JWT: the
// token still travels in the #fragment of the redirect alone.
// localStorage, not window.storage: its cookie fallback would send the entry to
// the server with every request; without localStorage the flow degrades to
// "return_to must survive in the URL".

export type AuthBridgeRequest = {
    next: string | null;
    returnTo: string;
};

type PendingAuthBridgeRequest = AuthBridgeRequest & {
    expiresAt: number;
};

export const PENDING_AUTH_BRIDGE_REQUEST_TTL_MS = 60 * 60 * 1000;

export const readAuthBridgeRequest = (url: URL): AuthBridgeRequest | null => {
    const returnTo = url.searchParams.get('return_to');

    if (!returnTo || url.searchParams.get('prepare_token') !== '1' || !isTrustedReturnToOrigin(returnTo)) {
        return null;
    }

    return { next: url.searchParams.get('next'), returnTo };
};

export const rememberAuthBridgeRequest = (): void => {
    const request = readAuthBridgeRequest(new URL(window.location.href));

    if (request) {
        storePendingRequest(request);
    }
};

// The most recent explicit sign-in intent wins. The login page opened without a
// trusted request is such an intent (e.g. the /de/adapt landing CTA, which leaves
// its resume path in the redirectLink cookie), so the login page drops an earlier
// request through this. A login form on any other page only remembers.
export const clearPendingAuthBridgeRequestUnlessInUrl = (): void => {
    if (!readAuthBridgeRequest(new URL(window.location.href))) {
        clearPendingAuthBridgeRequest();
    }
};

export const takePendingAuthBridgeRequest = (): AuthBridgeRequest | null => {
    const request = peekPendingRequest();

    clearPendingAuthBridgeRequest();

    return request;
};

// The password-reset tab ends on the login page. Carrying the remembered request
// in that URL continues the consumer's sign-in instead of clearing it.
export const getLoginPathKeepingAuthBridgeRequest = (): string => {
    const request = peekPendingRequest();

    if (!request) {
        return Routes.LOGIN.path;
    }

    const loginParams = new URLSearchParams({ prepare_token: '1', return_to: request.returnTo });

    if (request.next !== null) {
        loginParams.set('next', request.next);
    }

    return `${Routes.LOGIN.path}?${loginParams}`;
};

export const clearPendingAuthBridgeRequest = (): void => {
    try {
        window.localStorage.removeItem(storageKeys.auth.pendingAuthBridgeRequest);
    } catch {
        // Storage blocked — nothing could have been stored either.
    }
};

const storePendingRequest = (request: AuthBridgeRequest): void => {
    const pendingRequest: PendingAuthBridgeRequest = {
        ...request,
        expiresAt: Date.now() + PENDING_AUTH_BRIDGE_REQUEST_TTL_MS
    };

    try {
        window.localStorage.setItem(storageKeys.auth.pendingAuthBridgeRequest, JSON.stringify(pendingRequest));
    } catch {
        // Storage blocked — the direct path (return_to still in the URL) keeps working.
    }
};

const peekPendingRequest = (): AuthBridgeRequest | null => {
    let storedValue: string | null;

    try {
        storedValue = window.localStorage.getItem(storageKeys.auth.pendingAuthBridgeRequest);
    } catch {
        return null;
    }

    return storedValue === null ? null : parseValidPendingRequest(storedValue);
};

const parseValidPendingRequest = (storedValue: string): AuthBridgeRequest | null => {
    let pendingRequest: unknown;

    try {
        pendingRequest = JSON.parse(storedValue);
    } catch {
        return null;
    }

    if (!isPendingAuthBridgeRequest(pendingRequest) || !isUnexpired(pendingRequest.expiresAt)) {
        return null;
    }

    if (!isTrustedReturnToOrigin(pendingRequest.returnTo)) {
        return null;
    }

    return { next: pendingRequest.next, returnTo: pendingRequest.returnTo };
};

const isPendingAuthBridgeRequest = (value: unknown): value is PendingAuthBridgeRequest =>
    typeof value === 'object' &&
    value !== null &&
    'expiresAt' in value &&
    'next' in value &&
    'returnTo' in value &&
    typeof value.expiresAt === 'number' &&
    typeof value.returnTo === 'string' &&
    (value.next === null || typeof value.next === 'string');

const isUnexpired = (expiresAt: number): boolean => {
    const now = Date.now();

    return expiresAt > now && expiresAt <= now + PENDING_AUTH_BRIDGE_REQUEST_TTL_MS;
};
